import { useIssuesStore } from "@/store/zustand/issues-store";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";
import { FeaturePRD } from "@/types/issues";
import { useCanvasId } from "@/utils/utils";
import _ from "lodash";
import { useCallback } from "react";

const DEBOUNCE_DELAY_MS = 1000;

const debouncedEditSupabase = _.debounce(
  async (
    supabaseClient: any,
    field: string,
    newValue: any,
    canvasId: string,
    featurePrd: FeaturePRD | null,
    updateFeaturePRD: any,
  ) => {
    updateFeaturePRD(supabaseClient, {
      id: featurePrd?.id,
      canvas_id: canvasId,
      [field]: newValue,
    });
  },
  DEBOUNCE_DELAY_MS,
  { leading: true, trailing: true },
);

export const useEditPrd = (canvasId?: string) => {
  const urlCanvasId = useCanvasId();
  const effectiveCanvasId = canvasId || urlCanvasId;

  const updateFeaturePRD = useIssuesStore((state) => state.updateFeaturePRD);
  const currentIssue = useIssuesStore(
    (state) => state.canvasIdToIssue[effectiveCanvasId ?? ""],
  );

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  return useCallback(
    async (field: string, newValue: any, debounceSupabase?: boolean) => {
      if (!supabaseClient || !currentIssue) {
        return;
      }

      const featurePrd = currentIssue.featurePrd;

      if (!featurePrd) {
        return;
      }

      if (newValue === featurePrd[field]) {
        return;
      }

      if (debounceSupabase) {
        debouncedEditSupabase(
          supabaseClient,
          field,
          newValue,
          effectiveCanvasId,
          featurePrd,
          updateFeaturePRD,
        );
      } else {
        updateFeaturePRD(supabaseClient, {
          id: featurePrd?.id,
          canvas_id: effectiveCanvasId,
          [field]: newValue,
        });
      }
    },
    [supabaseClient, effectiveCanvasId, currentIssue, updateFeaturePRD],
  );
};
